import React, { useState, useEffect, useRef } from "react";
import { Popper, Fade, Paper, Typography, Button, Box } from "@mui/material";
import { Icon } from "@iconify/react";
import SiAutocomplete from "../si-autocomplete";
import AddIcon from "@mui/icons-material/Add";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CustomButtonComponent from "../custom-button";
import FILTER_ICON from "../../resources/icons/filter.png";

import "./index.scss";
import { convertCamelCaseToDisplayText } from "../../utils";

const CustomFilters = ({
  applyFilters,
  buttonText = "Filter",
  renderFilterInput,
  filterTypes,
  filterType,
  setFilterType,
  filterValue,
  setFilterValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const isSmallScreen = window.innerWidth <= 1024;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  // Apply filters
  const onApplyFilters = () => {
    const filters =
      selectedFilters.length > 0
        ? selectedFilters
        : filterType && filterValue
        ? [{ type: filterType, value: filterValue }]
        : [];
    if (selectedFilters.length < 1 && filterType && filterValue) {
      addFilter();
    }
    if (filters.length > 0) {
      const filtersObj = filters.reduce((acc, filter) => {
        acc[filter.type] = filter.value;
        return acc;
      }, {});
      applyFilters(filtersObj);
      setOpen(false);
    }
  };

  // Clear selected filters
  const onClearSelectedFilters = () => {
    setSelectedFilters([]);
    setFilterType("");
    setFilterValue("");
    setOpen(false);
    applyFilters({});
  };

  // Add filter
  const addFilter = () => {
    if (filterType && filterValue) {
      setSelectedFilters([
        ...selectedFilters,
        { type: filterType, value: filterValue },
      ]);
      setFilterValue("");
      setFilterType("");
    }
  };

  // Remove a filter chip
  const removeFilter = (type) => {
    const updatedFilters = selectedFilters.filter(
      (filter) => filter.type !== type
    );
    setSelectedFilters(updatedFilters);
    applyFilters(
      updatedFilters.reduce((acc, filter) => {
        acc[filter.type] = filter.value;
        return acc;
      }, {})
    );
  };

  // Handle filter type selection
  const handleFilterTypeChange = (val) => {
    if (val) {
      setFilterType(val.value);
      setFilterValue(""); // Clear previous value
    } else {
      setFilterType("");
      setFilterValue(""); // Clear previous value
    }
  };

  // Filter out selected filter types from filterTypes list
  const availableFilterTypes = filterTypes.filter(
    (filter) =>
      !selectedFilters.some((selected) => selected.type === filter.value)
  );
  useEffect(() => {
    setTimeout(() => {
      const filtersObject = sessionStorage.getItem("pastTicketsFiltersObject");
      const parsedFiltersObject = filtersObject
        ? JSON.parse(filtersObject)
        : {};

      if (Object.keys(parsedFiltersObject).length > 0) {
        const newSelectedFilters = Object.keys(parsedFiltersObject)?.map(
          (key) => ({
            type: key,
            value: parsedFiltersObject[key],
          })
        );
        setSelectedFilters(newSelectedFilters);
      }
    }, 500);
  }, []);

  return (
    <div className="ticket-filter-container">
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={isSmallScreen ? "bottom-start" : "right-start"}
        transition
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={{ padding: "10px" }} className="filter-popover-paper">
              <div className="filter-form">
                <Typography sx={{ pb: 1 }}>Select Filter Type</Typography>
                <SiAutocomplete
                  placeholder="Filter By"
                  value={filterTypes.find((ele) => ele.value === filterType)}
                  onChange={handleFilterTypeChange}
                  options={availableFilterTypes}
                />

                <Box sx={{ mt: 2 }}>{renderFilterInput()}</Box>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ marginTop: "16px", textTransform: "capitalize" }}
                  onClick={addFilter}
                >
                  New Filter
                </Button>
              </div>

              <div className="ticket-filter-buttons">
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={onClearSelectedFilters}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onApplyFilters}
                  startIcon={
                    <Icon
                      icon="tabler:filter-search"
                      width="1.2rem"
                      height="1.2rem"
                      style={{ color: "white" }}
                    />
                  }
                >
                  Apply
                </Button>
              </div>
            </Paper>
          </Fade>
        )}
      </Popper>

      <div className="filter-con">
        <img
          src={FILTER_ICON}
          alt="filter"
          width="15px"
          height="15px"
          style={{ marginRight: "10px" }}
        />
        {selectedFilters.map((filter, index) => (
          <div
            key={index}
            className={`chip-com-con ${
              Math.random() > 0.5 ? "purple-bg" : "orange-bg"
            }`}
          >
            <p>
              {`${convertCamelCaseToDisplayText(filter.type)}: ${
                typeof filter.value === "object"
                  ? filter?.value?.label
                  : filter.value
              }`}
            </p>
            <CloseOutlinedIcon
              onClick={() => {
                removeFilter(filter.type);
              }}
              fontSize="small"
              sx={{ ml: 1, cursor: "pointer" }}
            />
          </div>
        ))}
        <CustomButtonComponent
          startIcon={<AddIcon />}
          onClick={handleClick}
          sx={{ minWidth: "fit-content !important" }}
          variant="text"
        >
          {buttonText}
        </CustomButtonComponent>
      </div>
    </div>
  );
};

export default CustomFilters;
